import axios from 'axios';
import { BASE_URL, constructMediaUrl } from './helpers';
import { mediaThumbUrl } from './mediaUrls';

// ==================== Types ====================

/** One remembered fact about the series. `auto` rows are written by the pipeline
 *  itself (episode summaries, reviewer findings); `user` rows are typed into the
 *  Series settings modal. */
export interface SeriesMemory {
  id: string;
  text: string;
  created_at: string;
  source: 'user' | 'auto';
}

/** What fills one upload slot of the bound pipeline. */
export type SlotSource =
  | { source: 'media'; media_id: string }
  | { source: 'cast'; character_id: string }
  | { source: 'episode:last_frame' };

/** Upload checkpoint id → the sources that fill it, in order. */
export type SlotMap = Record<string, SlotSource[]>;

/** Upload checkpoint id → the character that fills it for ONE episode. */
export type SlotAssignment = Record<string, string>;

/**
 * One upload checkpoint of the bound pipeline, seen as a cast slot ("villain",
 * "victim", "place"). Only GET /series/:id returns them, in the pipeline's
 * checkpoint order, and none when no pipeline (or no upload) is bound.
 */
export interface SeriesSlot {
  checkpoint_id: string;
  /** upload.cast_name || checkpoint name || checkpoint id. */
  label: string;
  /** upload.role, default "seed_image". */
  role: string;
  /** upload.media_type, default "image"; "audio" for a voice_reference slot. */
  media_type: string;
  /** The pipeline needs at least one upload here to run. */
  required: boolean;
  /** 0 = unlimited. */
  max_count: number;
  /** Characters the slot map binds here ("cast" sources), in order. */
  character_ids: string[];
  /** The slot map has ANY source for this checkpoint — cast, media or last frame. */
  filled: boolean;
}

/** A non-blocking finding the store reports on GET /series/:id. The code is a
 *  stable identifier — today `template_missing` and `character_no_image` — and
 *  whatever the finding is *about* (a cast member's name) rides in `subject`,
 *  never inside the code. `detail` is free-text the UI may append; for
 *  `slot_unmapped` it is the slot's label while `subject` stays the checkpoint
 *  id the Fix-in-Builder link needs. */
export interface SeriesWarning {
  code: string;
  subject?: string;
  detail: string;
}

export interface Series {
  id: string;
  name: string;
  description: string;
  concept: string;
  /**
   * The series' format brief: genre, tone, length and how an episode is built,
   * in the owner's words (at most 8000 characters, kept as typed). It is the only
   * place a series' genre or style comes from. Empty when none was written; a
   * store that predates the field leaves it out, so read it as `?? ''`.
   */
  format_brief: string;
  /** The pipeline every episode of this series runs. */
  template_id: string | null;
  memories: SeriesMemory[];
  slot_map: SlotMap;
  /** Values for the bound pipeline's declared run parameters. */
  parameters: Record<string, string>;
  metadata: Record<string, unknown>;
  /** Only returned by GET /series/:id — list rows carry none. */
  warnings?: SeriesWarning[];
  /** The bound pipeline's upload checkpoints as cast slots. GET /series/:id only. */
  slots?: SeriesSlot[];
  created_at: string;
  updated_at: string;
}

export type CharacterKind = 'character' | 'place' | 'prop';

export interface Character {
  id: string;
  series_id: string;
  name: string;
  description: string;
  voice: string;
  /** What this cast entry is — drives slot-picker labels and the cast sheet. */
  kind: CharacterKind;
  /** Short invariant phrases that must survive every regeneration. */
  anchors: string[];
  /** UUID FK to media_items holding this character's voice sample. */
  voice_media_id?: string | null;
  /** UUID FK to media_items */
  reference_image_media_id?: string | null;
  /** UUID of a published generator profile, or null when no identity is trained */
  generator_profile_id?: string | null;
  /** metadata holds reference_image_url for display */
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export type EpisodeStatus = 'draft' | 'generating' | 'complete' | 'failed';

export interface EpisodeMetadata {
  status?: EpisodeStatus;
  run_id?: string;
  clip_id?: string;
  character_ids?: string[];
  initial_context?: string;
  error?: string;
  /** Who filled each upload slot, when the episode was started with picks. */
  slot_assignment?: SlotAssignment;
  [key: string]: unknown;
}

/** A name an episode's plan or uploads introduced that is not cast yet. */
export interface EpisodeCastCandidate {
  name: string;
  anchors: string[];
  media_id?: string;
  source: 'plan' | 'upload';
}

/** The same candidate as the series-level endpoint reports it — with the
 *  episode it surfaced in. */
export interface CastCandidate extends EpisodeCastCandidate {
  episode_number: number;
  /** The episode's id, when the endpoint names it. Preferred over resolving the
   *  number against the page's episode list — promote is addressed by id. */
  episode_id?: string;
}

/** What the episode leaves behind for the next one: the state the series is in
 *  when it ends, and the hook the next episode may continue from. */
export interface EpisodeStoryline {
  summary: string;
  character_states: Record<string, string>;
  open_threads: string[];
  last_shot: string;
  teaser: string;
  next_hook: string;
  /** Whether this episode picked up the previous episode's hook — what the
   *  engine reads to decide whether a previous last frame carries over. */
  continues_from_hook: boolean;
  cast_candidates?: EpisodeCastCandidate[];
}

export interface Episode {
  id: string;
  series_id: string;
  episode_number: number;
  title: string;
  synopsis: string;
  prev_episode_summary: string;
  status: EpisodeStatus;
  run_id?: string;
  idea_id?: string;
  clip_id?: string;
  storyline: EpisodeStoryline;
  last_frame_media_id?: string;
  metadata: EpisodeMetadata;
  created_at: string;
  updated_at: string;
}

/**
 * Returns the display URL for a character's reference image. Prefers the raw
 * URL stashed in metadata; falls back to the thumb route built off
 * `reference_image_media_id`. The fallback matters for a PROMOTED character —
 * cast promotion writes the media id straight onto the row without ever
 * populating `metadata.reference_image_url` — so without it a promoted
 * character's portrait would be blank in the cast strip and editor preview
 * despite the reference image existing in the media library.
 */
export function characterImageUrl(char: Character): string | undefined {
  const url = char.metadata?.reference_image_url;
  if (typeof url === 'string' && url) return constructMediaUrl(url) || undefined;
  return char.reference_image_media_id ? mediaThumbUrl(char.reference_image_media_id) || undefined : undefined;
}

/** The extra reference images stored on a cast member, tolerant of rows written
 *  before the field existed (absent) and of a non-list value. */
export function characterReferenceMediaIds(char: Character): string[] {
  const raw = char.metadata?.reference_media_ids;
  if (!Array.isArray(raw)) return [];
  return raw.filter((id): id is string => typeof id === 'string' && id.length > 0);
}

/**
 * The three fields that were once metadata and are now real columns. Rows
 * written before the migration still carry only the metadata copy, so the
 * column is read first and metadata is the fallback — never the other way
 * round, or a stale metadata blob would outrank the live column.
 */
/** `||`, not `??`, and the same for its two siblings below: the Go DTO declares
 *  these fields without `omitempty`, so an absent value arrives as `""` rather
 *  than as `undefined`, and `??` would let the empty string win. */
export const episodeStatus = (episode: Episode): EpisodeStatus =>
  (episode.status || episode.metadata?.status || 'draft') as EpisodeStatus;

export const episodeRunId = (episode: Episode): string | undefined =>
  episode.run_id || episode.metadata?.run_id || undefined;

export const episodeClipId = (episode: Episode): string | undefined =>
  episode.clip_id || episode.metadata?.clip_id || undefined;

// ==================== Series ====================

export const listSeries = (): Promise<Series[]> =>
  axios.get(`${BASE_URL}/series`).then((r) => {
    const d = r.data;
    return Array.isArray(d) ? d : (d?.items ?? []);
  });

export const getSeries = (id: string): Promise<Series> =>
  axios.get(`${BASE_URL}/series/${id}`).then((r) => r.data);

export const createSeries = (data: {
  name: string;
  concept: string;
  description?: string;
  /** Required by the UI: a series is born bound to the pipeline its episodes run. */
  template_id: string;
}): Promise<Series> =>
  axios.post(`${BASE_URL}/series`, {
    name: data.name,
    description: data.description ?? '',
    concept: data.concept,
    template_id: data.template_id,
    memories: [],
    slot_map: {},
    parameters: {},
    metadata: {},
  }).then((r) => r.data);

type SeriesUpdatableField =
  | 'name'
  | 'description'
  | 'concept'
  | 'format_brief'
  | 'template_id'
  | 'memories'
  | 'slot_map'
  | 'parameters'
  | 'metadata';

/**
 * Full-object update — series_id not required by store for series. Fields the
 * caller leaves out keep their current value; `template_id: null` is an
 * explicit unbind, which is why it is read by key presence, not by `??`.
 * `format_brief` is the exception: left out, it is not sent at all.
 */
export const updateSeries = (
  existing: Series,
  updates: Partial<Pick<Series, SeriesUpdatableField>>
): Promise<Series> =>
  axios.put(`${BASE_URL}/series/${existing.id}`, {
    name: updates.name ?? existing.name,
    description: updates.description ?? existing.description,
    concept: updates.concept ?? existing.concept,
    // Only when the caller means to write it: the store keeps the stored brief
    // when the key is absent, so a copy of the series read before the brief was
    // rewritten (in the chat, say) cannot put the old text back by saving
    // something else.
    ...('format_brief' in updates ? { format_brief: updates.format_brief } : {}),
    template_id: 'template_id' in updates ? updates.template_id : existing.template_id ?? null,
    memories: updates.memories ?? existing.memories ?? [],
    slot_map: updates.slot_map ?? existing.slot_map ?? {},
    parameters: updates.parameters ?? existing.parameters ?? {},
    metadata: updates.metadata ?? existing.metadata,
  }).then((r) => r.data);

export const deleteSeries = (id: string): Promise<void> =>
  axios.delete(`${BASE_URL}/series/${id}`).then(() => undefined);

// ==================== Characters ====================

export const listCharacters = (seriesId: string): Promise<Character[]> =>
  axios.get(`${BASE_URL}/characters`, { params: { series_id: seriesId } }).then((r) => {
    const d = r.data;
    return Array.isArray(d) ? d : (d?.items ?? []);
  });

export const createCharacter = (data: {
  series_id: string;
  name: string;
  description?: string;
  voice?: string;
  kind?: CharacterKind;
  anchors?: string[];
  /** UUID of the uploaded voice sample, or null when there is none. */
  voice_media_id?: string | null;
  generator_profile_id?: string | null;
  reference_image_media_id?: string;
  /** stored in metadata.reference_image_url for display */
  reference_image_url?: string;
  /** Extra reference images, stored in metadata.reference_media_ids. */
  reference_media_ids?: string[];
}): Promise<Character> =>
  axios.post(`${BASE_URL}/characters`, {
    series_id: data.series_id,
    name: data.name,
    description: data.description ?? '',
    voice: data.voice ?? '',
    kind: data.kind ?? 'character',
    anchors: data.anchors ?? [],
    voice_media_id: data.voice_media_id ?? null,
    generator_profile_id: data.generator_profile_id ?? null,
    reference_image_media_id: data.reference_image_media_id ?? null,
    metadata: {
      reference_image_url: data.reference_image_url ?? '',
      reference_media_ids: data.reference_media_ids ?? [],
    },
  }).then((r) => r.data);

/**
 * Update a character. Takes the existing character so required fields
 * (series_id) are always present in the PUT body.
 */
export const updateCharacter = (
  existing: Character,
  updates: {
    name?: string;
    description?: string;
    voice?: string;
    kind?: CharacterKind;
    anchors?: string[];
    /** UUID of the voice sample, or null to drop it. Read by key presence, like
     *  its two nullable siblings: the backend forwards an explicit null. */
    voice_media_id?: string | null;
    reference_image_media_id?: string | null;
    /** UUID of a published generator profile (or null to unlink) */
    generator_profile_id?: string | null;
    /** stored in metadata.reference_image_url */
    reference_image_url?: string;
    /** stored in metadata.reference_media_ids */
    reference_media_ids?: string[];
  }
): Promise<Character> =>
  axios.put(`${BASE_URL}/characters/${existing.id}`, {
    series_id: existing.series_id,
    name: updates.name ?? existing.name,
    description: updates.description ?? existing.description,
    voice: updates.voice ?? existing.voice,
    kind: updates.kind ?? existing.kind ?? 'character',
    anchors: updates.anchors ?? existing.anchors ?? [],
    voice_media_id:
      'voice_media_id' in updates ? updates.voice_media_id : existing.voice_media_id ?? null,
    reference_image_media_id:
      'reference_image_media_id' in updates
        ? updates.reference_image_media_id
        : existing.reference_image_media_id,
    generator_profile_id:
      'generator_profile_id' in updates
        ? updates.generator_profile_id
        : existing.generator_profile_id ?? null,
    metadata: {
      ...existing.metadata,
      ...(updates.reference_image_url !== undefined
        ? { reference_image_url: updates.reference_image_url }
        : {}),
      ...(updates.reference_media_ids !== undefined
        ? { reference_media_ids: updates.reference_media_ids }
        : {}),
    },
  }).then((r) => r.data);

export const deleteCharacter = (id: string): Promise<void> =>
  axios.delete(`${BASE_URL}/characters/${id}`).then(() => undefined);

// ==================== Episodes ====================

export const listEpisodes = (seriesId: string): Promise<Episode[]> =>
  axios.get(`${BASE_URL}/episodes`, { params: { series_id: seriesId } }).then((r) => {
    const d = r.data;
    return Array.isArray(d) ? d : (d?.items ?? []);
  });

export const deleteEpisode = (id: string): Promise<void> =>
  axios.delete(`${BASE_URL}/episodes/${id}`).then(() => undefined);

/** The episode fields a sparse update may name. `episode_number` is excluded —
 *  the store and backend reject it on PATCH, since it is the episode's
 *  ordering key within the series, not a field a writer amends. `metadata` is
 *  excluded too: a PATCH replaces it WHOLE, and the backend writes keys into it
 *  (the episode's `slot_assignment`, its run) that a copy held by the page
 *  may not have yet — sending one would wipe them. */
export type EpisodePatch = Partial<
  Pick<
    Episode,
    | 'title'
    | 'synopsis'
    | 'prev_episode_summary'
    | 'status'
    | 'run_id'
    | 'idea_id'
    | 'clip_id'
    | 'last_frame_media_id'
    | 'storyline'
  >
>;

/**
 * Sparse episode update: ONLY the keys given are sent, and the handler re-reads
 * the row afterwards, so the answer is the WHOLE episode however little was
 * patched. A writer that knows one field (the Studio writing back an assembled
 * `clip_id`) can therefore say so without holding — and re-sending — the rest
 * of the row.
 */
export const patchEpisode = (episodeId: string, patch: EpisodePatch): Promise<Episode> =>
  axios.patch(`${BASE_URL}/episodes/${episodeId}`, patch).then((r) => r.data);

// ==================== Episode start ====================

export interface StartEpisodeRequest {
  /** Optional steer for this episode — the series' own memory supplies the rest. */
  nudge?: string;
  title?: string;
  /** Cast members to force into the episode; omitted = the backend decides. */
  cast_member_ids?: string[];
  /** Which character fills each named upload slot for this episode, replacing
   *  the slot map's sources there; omitted = the slot map decides. */
  slot_assignment?: SlotAssignment;
}

export interface StartEpisodeResponse {
  episode: Episode;
  run_id: string;
  idea_id: string;
}

/**
 * One call starts an episode: the backend composes the input from the series'
 * concept, memories, cast and the previous episode's storyline, creates the
 * episode row and starts the run.
 */
export const startEpisode = (
  seriesId: string,
  body: StartEpisodeRequest = {}
): Promise<StartEpisodeResponse> =>
  axios.post(`${BASE_URL}/series/${seriesId}/episodes/start`, body).then((r) => r.data);

// ==================== Cast promotion ====================

/**
 * One candidate, safe to render. Go marshals a nil `anchors` slice as `null`
 * (the DTO carries no `omitempty`), and a plan-sourced candidate normally has
 * none — so `anchors: string[]` is a promise this boundary KEEPS rather than a
 * claim every read site has to defend. `media_id`/`episode_id` are optional the
 * same way: an empty string is no id, and reads treat it as absent.
 */
const normaliseCastCandidate = (raw: Partial<CastCandidate> | null | undefined): CastCandidate => ({
  ...(raw as CastCandidate),
  anchors: Array.isArray(raw?.anchors)
    ? raw!.anchors.filter((anchor): anchor is string => typeof anchor === 'string')
    : [],
  media_id: raw?.media_id || undefined,
  episode_id: raw?.episode_id || undefined,
});

/** Names the series' episodes introduced that have no Character row yet. */
export const getCastCandidates = (seriesId: string): Promise<{ candidates: CastCandidate[] }> =>
  axios.get(`${BASE_URL}/series/${seriesId}/cast/candidates`).then((r) => ({
    candidates: (Array.isArray(r.data?.candidates) ? r.data.candidates : []).map(
      normaliseCastCandidate
    ),
  }));

/** Turns named candidates of one episode into Characters of the series. */
export const promoteCast = (
  seriesId: string,
  body: { episode_id: string; names: string[] }
): Promise<{ characters: Character[] }> =>
  axios
    .post(`${BASE_URL}/series/${seriesId}/cast/promote`, body)
    .then((r) => ({ characters: r.data?.characters ?? [] }));
